import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { Link } from "react-router-dom";
import { FaArrowRight, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { getBanners } from "../../slice/bannerSlice";
import { getMediaUrl } from "../../common/mediaUrl";
import { useTranslation } from "react-i18next";

const AUTO_DELAY = 6000;

const HeroAutoSlider = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { data: banners, loading } = useSelector((state) => state.banner);
  
  const [index, setIndex] = useState(0);
  const total = banners?.length || 0;

  const titleRef = useRef(null);
  const descRef = useRef(null);
  const buttonsRef = useRef(null);
  const tagRef = useRef(null);
  const lineRef = useRef(null);

  useEffect(() => {
    dispatch(getBanners());
  }, [dispatch, i18n.language]);

  /* AUTO SLIDER */
  useEffect(() => {
    if (total <= 1) return;
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, AUTO_DELAY);
    return () => clearInterval(interval);
  }, [total]);

  /* TEXT ANIMATION */
  useEffect(() => {
    if (!banners || banners.length === 0) return;
    if (!tagRef.current || !titleRef.current || !descRef.current || !buttonsRef.current || !lineRef.current) return;
    
    const ctx = gsap.context(() => {
      // Reset line width
      gsap.set(lineRef.current, { width: 0 });

      const tl = gsap.timeline();

      tl.fromTo(
        [tagRef.current, titleRef.current, descRef.current, buttonsRef.current],
        { opacity: 0, y: 40, filter: "blur(15px)" },
        {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 1.4,
          ease: "expo.out",
          stagger: 0.15,
        }
      ).to(
        lineRef.current,
        { width: 64, duration: 1, ease: "power2.out" },
        "-=1"
      );
    });
    return () => ctx.revert();
  }, [index, banners]);

  const handlePrev = () => setIndex((prev) => (prev - 1 + total) % total);
  const handleNext = () => setIndex((prev) => (prev + 1) % total);

  if (loading && total === 0) {
    return (
      <div className="h-[93svh] w-full bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-orange-main border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!banners || banners.length === 0) {
    return null; // Or a fallback hero section
  }

  const active = banners[index];
  return (
    <section className="relative h-[93svh] w-full overflow-hidden bg-[#050505]">
      {/* BACKGROUND IMAGE WITH IMMERSIVE ZOOM */}
      <div className="absolute inset-0 z-0">
        {banners.map((slide, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-all duration-[1.5s] ease-out ${i === index ? "opacity-100 scale-100" : "opacity-0 scale-110"
              }`}
          >
            <img
              src={getMediaUrl(slide.image)}
              alt={slide.title ? `Khajuraho Routes - ${slide.title}` : "Khajuraho Tour Package Banner"}
              className="w-full h-full object-cover"
            />
            {/* Cinematic Overlays */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
            <div className="absolute inset-0 shadow-[inset_0_0_150px_rgba(0,0,0,0.5)]" />
          </div>
        ))}
      </div>

      {/* CONTENT AREA */}
      <div className="relative z-10 flex h-full items-center">
        <div className="mx-auto w-full max-w-7xl px-6 lg:px-12">
          <div className="max-w-3xl">
            {/* DYNAMIC TAG */}
            <div
              ref={tagRef}
              className="mb-8 flex items-center gap-4"
            >
              <div ref={lineRef} className="h-[2px] bg-orange-main" />
              <span className="text-xs font-bold uppercase tracking-[0.5em] text-orange-main drop-shadow-sm">
                {active.title}
              </span>
            </div>

            {/* HIGH-END TITLE */}
            <h1
              ref={titleRef}
              className="mb-8 text-3xl lg:text-6xl font-black leading-[1.05] text-white tracking-tighter"
            >
              {active.heading}
            </h1>

            {/* DESCRIPTION */}
            <p
              ref={descRef}
              className="mb-12 text-[18px] text-white/60 leading-relaxed font-medium max-w-2xl border-l-2 border-white/5 pl-2"
            >
              {active.description}
            </p>

            {/* ACTION BUTTONS */}
            <div
              ref={buttonsRef}
              className="flex flex-wrap gap-5"
            >
              <Link to="/tours">
                <button className="group relative overflow-hidden rounded-full bg-orange-main px-7 py-3.5 lg:px-10 lg:py-4 text-[11px] lg:text-[13px] font-black uppercase tracking-[0.15em] text-white shadow-[0_15px_30px_rgba(217,116,42,0.3)] transition-all hover:shadow-[0_20px_40px_rgba(217,116,42,0.5)] hover:-translate-y-1 active:scale-95">
                  <span className="relative z-10 flex items-center gap-3">
                    {t('hero_explore')}
                    <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center transition-transform group-hover:bg-white group-hover:text-orange-main group-hover:translate-x-1">
                      <FaArrowRight className="text-[10px]" />
                    </div>
                  </span>
                  <div className="absolute inset-0 z-0 bg-gradient-to-r from-orange-light to-orange-main opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* REFINED NAVIGATION ARROWS */}
      <div className="absolute inset-0 z-20 pointer-events-none">
        <div className="h-full w-full relative flex items-center justify-between px-4 lg:px-8">
          <button
            onClick={handlePrev}
            className="pointer-events-auto flex h-11 w-11 lg:h-16 lg:w-16 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur-xl transition-all hover:bg-orange-main hover:border-orange-main hover:scale-110 shadow-2xl group/prev opacity-70 hover:opacity-100"
          >
            <FaChevronLeft className="text-base lg:text-xl transition-transform group-hover/prev:-translate-x-1" />
          </button>
          <button
            onClick={handleNext}
            className="pointer-events-auto flex h-11 w-11 lg:h-16 lg:w-16 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur-xl transition-all hover:bg-orange-main hover:border-orange-main hover:scale-110 shadow-2xl group/next opacity-70 hover:opacity-100"
          >
            <FaChevronRight className="text-base lg:text-xl transition-transform group-hover/next:translate-x-1" />
          </button>
        </div>
      </div>

      {/* SIDE DECOR - VERTICAL TEXT */}
      <div className="absolute right-12 top-1/2 hidden -translate-y-1/2 rotate-90 lg:block opacity-20 pointer-events-none">
        <span className="text-[9px] font-bold uppercase tracking-[1.2em] text-white whitespace-nowrap">
          {t('hero_side_text')}
        </span>
      </div>
    </section>
  );
};

export default HeroAutoSlider;
